import { create } from "zustand";

import { getApi } from "./get-api";
import {
  parsePreviewBody,
  previewFailureMessage,
  PreviewView,
} from "./preview-as";
import { useAuth } from "./useAuth";

type PreviewSession = {
  datasourceId: string | null;
  memberId: string | null;
  view: PreviewView | null;
  error: string | null;
  loading: boolean;
  load: (datasourceId: string, memberId: string) => Promise<void>;
  clear: () => void;
};

let requestSerial = 0;

export const usePreviewSession = create<PreviewSession>((set) => ({
  datasourceId: null,
  memberId: null,
  view: null,
  error: null,
  loading: false,
  clear: () => {
    requestSerial += 1;
    set({
      datasourceId: null,
      memberId: null,
      view: null,
      error: null,
      loading: false,
    });
  },
  load: async (datasourceId, memberId) => {
    const serial = ++requestSerial;
    set({
      datasourceId,
      memberId,
      view: null,
      error: null,
      loading: true,
    });
    try {
      const token = useAuth.getState().token;
      const api = getApi({ token });
      const response = await (
        api as {
          request: (params: {
            path: string;
            method: string;
            query: { previewAs: string };
            format: string;
          }) => Promise<{ data: unknown }>;
        }
      ).request({
        path: `/datasources/${encodeURIComponent(datasourceId)}/preview`,
        method: "GET",
        query: { previewAs: memberId },
        format: "json",
      });
      if (serial !== requestSerial) return;
      const view = parsePreviewBody(response.data);
      if (!view) {
        set({
          loading: false,
          view: null,
          error: "Could not read this preview.",
        });
        return;
      }
      set({ loading: false, view, error: null });
    } catch (error) {
      if (serial !== requestSerial) return;
      set({
        loading: false,
        view: null,
        error:
          previewFailureMessage(error) || "Could not open this preview.",
      });
    }
  },
}));
